// ===================================================================
// KAIXO - Pestaña Configuración (solo admin_general)
// ===================================================================
// Lista de admins · asignar/revocar roles · resetear contraseñas
// de área. Todas las llamadas son a endpoints protegidos por
// requireRole('admin_general').
// ===================================================================

import React, { useEffect, useState, useCallback, useRef } from 'react';
import {
    adminListAdmins,
    adminGrantRole,
    adminRevokeRole,
    adminResetAreaPassword,
    adminUsersList,
    adminRemoveAdmin
} from '../../../services/adminApi';
import './AdminConfigSection.css';

const ROLES = [
    { id: 'admin_general',      label: 'General' },
    { id: 'admin_support',      label: 'Soporte' },
    { id: 'admin_verification', label: 'Verificaciones' },
    { id: 'admin_accounting',   label: 'Contabilidad' },
    { id: 'admin_maintenance',  label: 'Mantenimiento' }
];

// Roles asignables desde el buscador. admin_general queda fuera porque
// está bloqueado a un único superadmin (env SUPERADMIN_PHONE) y el
// backend rechaza con 403 cualquier intento de asignarlo a otro user.
const ASSIGNABLE_ROLES = ROLES.filter(r => r.id !== 'admin_general');
const AREAS = [
    { id: 'support',      label: 'Soporte' },
    { id: 'verification', label: 'Verificaciones' },
    { id: 'accounting',   label: 'Contabilidad' },
    { id: 'maintenance',  label: 'Mantenimiento' }
];

export default function AdminConfigSection() {
    const [admins, setAdmins] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');

    // ─── Buscador de usuarios para promocionar ──────────────────────
    const [searchQuery, setSearchQuery] = useState('');
    const [searchResults, setSearchResults] = useState([]);
    const [searching, setSearching] = useState(false);
    const debounceRef = useRef(null);

    // Modal "Promocionar"
    const [promoteUser, setPromoteUser] = useState(null); // user object | null
    const [promoteRole, setPromoteRole] = useState('admin_support');
    const [promotePassword, setPromotePassword] = useState('');
    const [promoting, setPromoting] = useState(false);
    const [promoteError, setPromoteError] = useState('');

    const reload = useCallback(async () => {
        setLoading(true);
        setError('');
        try {
            const { data } = await adminListAdmins();
            setAdmins(data?.admins || []);
        } catch (err) {
            setError(err.response?.data?.error || err.message);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { reload(); }, [reload]);

    // Debounce del buscador: dispara la query 300ms después de que el
    // usuario deje de escribir, para no saturar el endpoint.
    useEffect(() => {
        if (debounceRef.current) clearTimeout(debounceRef.current);
        const q = searchQuery.trim();
        if (!q || q.length < 2) {
            setSearchResults([]);
            setSearching(false);
            return;
        }
        setSearching(true);
        debounceRef.current = setTimeout(async () => {
            try {
                const { data } = await adminUsersList({ q, limit: 10 });
                // Filtrar usuarios que YA son admin (para no poder
                // "promocionarlos" otra vez desde aquí — sus roles se
                // gestionan en la tabla de abajo).
                const adminIds = new Set(admins.map(a => String(a._id)));
                const candidates = (data?.users || []).filter(u => !adminIds.has(String(u._id)));
                setSearchResults(candidates);
            } catch (err) {
                setSearchResults([]);
            } finally {
                setSearching(false);
            }
        }, 300);
        return () => { if (debounceRef.current) clearTimeout(debounceRef.current); };
    }, [searchQuery, admins]);

    const openPromote = (user) => {
        setPromoteUser(user);
        setPromoteRole('admin_support');
        setPromotePassword('');
        setPromoteError('');
    };

    const closePromote = () => {
        setPromoteUser(null);
        setPromoteRole('admin_support');
        setPromotePassword('');
        setPromoteError('');
    };

    const submitPromote = async () => {
        if (!promoteUser) return;
        if (promotePassword.length < 8) {
            setPromoteError('La contraseña de área debe tener al menos 8 caracteres');
            return;
        }
        const areaToPwd = promoteRole.replace('admin_', '');
        setPromoting(true);
        setPromoteError('');
        try {
            await adminGrantRole(promoteUser._id, promoteRole, { [areaToPwd]: promotePassword });
            closePromote();
            setSearchQuery('');
            setSearchResults([]);
            await reload();
        } catch (err) {
            setPromoteError(err.response?.data?.error || err.message);
        } finally {
            setPromoting(false);
        }
    };

    const handleGrantRole = async (userId) => {
        const role = prompt(`Rol a asignar:\n${ROLES.map(r => '- ' + r.id).join('\n')}`);
        if (!role || !ROLES.find(r => r.id === role)) {
            alert('Rol inválido');
            return;
        }
        let areaPasswords = null;
        if (role !== 'admin_general') {
            // Para roles no-general, asignamos la contraseña inicial del área
            const areaToPwd = role.replace('admin_', '');  // support, verification, etc.
            const pwd = prompt(`Contraseña inicial del área "${areaToPwd}" (mínimo 8 caracteres):`);
            if (!pwd || pwd.length < 8) {
                alert('Contraseña debe tener al menos 8 caracteres');
                return;
            }
            areaPasswords = { [areaToPwd]: pwd };
        }
        try {
            await adminGrantRole(userId, role, areaPasswords);
            reload();
        } catch (err) {
            alert(err.response?.data?.error || err.message);
        }
    };

    const handleRevokeRole = async (userId, role) => {
        if (!window.confirm(`¿Quitar el rol "${role}"?`)) return;
        try {
            await adminRevokeRole(userId, role);
            reload();
        } catch (err) {
            alert(err.response?.data?.error || err.message);
        }
    };

    const handleRemoveAdmin = async (admin) => {
        const name = admin.name || 'este usuario';
        const roles = (admin.adminRoles || []).join(', ') || 'legacy admin';
        const confirmText = `¿Quitar TODO el acceso admin a "${name}"?\n\n` +
            `Roles que pierde: ${roles}\n\n` +
            `Después tendrá que ser promocionado de nuevo desde el buscador. ` +
            `Esta acción queda registrada en auditoría.`;
        if (!window.confirm(confirmText)) return;
        try {
            await adminRemoveAdmin(admin._id);
            await reload();
        } catch (err) {
            alert(err.response?.data?.error || err.message);
        }
    };

    const handleResetPassword = async (userId, area) => {
        const pwd = prompt(`Nueva contraseña para área "${area}" (mínimo 8 caracteres):`);
        if (!pwd || pwd.length < 8) return;
        try {
            await adminResetAreaPassword(userId, area, pwd);
            alert('Contraseña reseteada. Comunícasela al admin afectado por canal seguro.');
        } catch (err) {
            alert(err.response?.data?.error || err.message);
        }
    };

    return (
        <div className="acfg-wrap">
            <div className="acfg-intro">
                <p><strong>Gestión de administradores.</strong> Solo accesible para administradores generales.</p>
                <p className="acfg-intro-sub">
                    Busca un usuario por nombre, email o teléfono y promociónalo a admin con un click. El rol{' '}
                    <code>admin_general</code> queda reservado al superadmin del producto.
                </p>
            </div>

            {/* Buscador para promocionar usuarios a admin */}
            <div className="acfg-promote">
                <input
                    type="text"
                    className="acfg-search-input"
                    placeholder="🔍 Buscar usuario por nombre, email o teléfono…"
                    value={searchQuery}
                    onChange={e => setSearchQuery(e.target.value)}
                />
                {searching && <div className="acfg-search-status">Buscando…</div>}
                {!searching && searchResults.length > 0 && (
                    <div className="acfg-search-results">
                        {searchResults.map(u => (
                            <button
                                key={u._id}
                                className="acfg-search-card"
                                onClick={() => openPromote(u)}
                                title="Promocionar a admin"
                            >
                                <div className="acfg-avatar">
                                    {u.avatar
                                        ? <img src={u.avatar} alt="" />
                                        : <span>{(u.name || '?')[0].toUpperCase()}</span>}
                                </div>
                                <div className="acfg-search-meta">
                                    <strong>{u.name || '—'}</strong>
                                    <span className="acfg-search-detail">
                                        {u.phone || u.email || u._id}
                                    </span>
                                </div>
                                <span className="acfg-search-add">+ admin</span>
                            </button>
                        ))}
                    </div>
                )}
                {!searching && searchQuery.trim().length >= 2 && searchResults.length === 0 && (
                    <div className="acfg-search-status">Sin resultados (o ya son admins).</div>
                )}
            </div>

            {loading && <div className="ap-loading">Cargando administradores…</div>}
            {error && <div className="ap-error">{error}</div>}

            {!loading && (
                <table className="acfg-table">
                    <thead>
                        <tr>
                            <th>Usuario</th>
                            <th>Email</th>
                            <th>Roles</th>
                            <th>Acciones</th>
                        </tr>
                    </thead>
                    <tbody>
                        {admins.map(a => (
                            <tr key={a._id}>
                                <td>
                                    <div className="acfg-user">
                                        <div className="acfg-avatar">
                                            {a.avatar
                                                ? <img src={a.avatar} alt="" />
                                                : <span>{(a.name || '?')[0].toUpperCase()}</span>}
                                        </div>
                                        <div>
                                            <strong>{a.name || '—'}</strong>
                                            <div className="acfg-uid">{a._id}</div>
                                        </div>
                                    </div>
                                </td>
                                <td>{a.email || '—'}</td>
                                <td>
                                    {(a.adminRoles || []).map(r => (
                                        <span key={r} className="acfg-role-chip">
                                            {ROLES.find(x => x.id === r)?.label || r}
                                            <button
                                                className="acfg-role-x"
                                                title="Revocar este rol"
                                                onClick={() => handleRevokeRole(a._id, r)}
                                            >×</button>
                                        </span>
                                    ))}
                                    {(!a.adminRoles || a.adminRoles.length === 0) && a.isAdmin && (
                                        <span className="acfg-role-chip acfg-role-legacy">Legacy admin</span>
                                    )}
                                </td>
                                <td>
                                    <div className="acfg-actions">
                                        <button
                                            className="acfg-btn"
                                            onClick={() => handleGrantRole(a._id)}
                                        >+ rol</button>
                                        {AREAS.map(area => (
                                            <button
                                                key={area.id}
                                                className="acfg-btn-small"
                                                onClick={() => handleResetPassword(a._id, area.id)}
                                                title={`Resetear contraseña de ${area.label}`}
                                            >
                                                🔑 {area.label}
                                            </button>
                                        ))}
                                        {/* "Quitar admin" elimina TODOS los roles + isAdmin=false
                                            en una sola op atómica. El backend bloquea el caso de
                                            destituir al superadmin (Víctor) y al propio admin que
                                            llama (auto-protección). */}
                                        <button
                                            className="acfg-btn-danger"
                                            onClick={() => handleRemoveAdmin(a)}
                                            title="Quitar todos los roles y dejar de ser admin"
                                        >
                                            🗑️ Quitar admin
                                        </button>
                                    </div>
                                </td>
                            </tr>
                        ))}
                        {admins.length === 0 && (
                            <tr><td colSpan={4} style={{ textAlign: 'center', padding: 24, color: '#888' }}>
                                Sin administradores registrados.
                            </td></tr>
                        )}
                    </tbody>
                </table>
            )}

            {/* Modal de promoción: pide rol + contraseña inicial del área */}
            {promoteUser && (
                <div className="acfg-modal-overlay" onClick={closePromote}>
                    <div className="acfg-modal" onClick={e => e.stopPropagation()}>
                        <div className="acfg-modal-head">
                            <h3>Promocionar a admin</h3>
                            <button className="acfg-modal-close" onClick={closePromote}>✕</button>
                        </div>
                        <div className="acfg-modal-body">
                            <div className="acfg-modal-user">
                                <div className="acfg-avatar">
                                    {promoteUser.avatar
                                        ? <img src={promoteUser.avatar} alt="" />
                                        : <span>{(promoteUser.name || '?')[0].toUpperCase()}</span>}
                                </div>
                                <div>
                                    <strong>{promoteUser.name}</strong>
                                    <div className="acfg-uid">{promoteUser.phone || promoteUser.email}</div>
                                </div>
                            </div>

                            <label className="acfg-modal-label">Rol</label>
                            <select
                                className="acfg-modal-select"
                                value={promoteRole}
                                onChange={e => setPromoteRole(e.target.value)}
                                disabled={promoting}
                            >
                                {ASSIGNABLE_ROLES.map(r => (
                                    <option key={r.id} value={r.id}>{r.label}</option>
                                ))}
                            </select>

                            <label className="acfg-modal-label">
                                Contraseña inicial del área (mín. 8 caracteres)
                            </label>
                            <input
                                type="text"
                                className="acfg-modal-input"
                                placeholder="ej. soporte-2026-kaixo"
                                value={promotePassword}
                                onChange={e => setPromotePassword(e.target.value)}
                                disabled={promoting}
                                onKeyDown={e => { if (e.key === 'Enter' && promotePassword.length >= 8) submitPromote(); }}
                            />
                            <p className="acfg-modal-hint">
                                Dale esta contraseña al admin por canal seguro. La pedirá al entrar a su pestaña por primera vez.
                            </p>

                            {promoteError && <div className="acfg-modal-error">{promoteError}</div>}
                        </div>
                        <div className="acfg-modal-actions">
                            <button className="acfg-btn-cancel" onClick={closePromote} disabled={promoting}>
                                Cancelar
                            </button>
                            <button
                                className="acfg-btn-primary"
                                onClick={submitPromote}
                                disabled={promoting || promotePassword.length < 8}
                            >
                                {promoting ? 'Asignando…' : 'Asignar rol'}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
